/**
 * Tiered quote (JF 2026-10-12): the customer wants prices for e.g. 100 / 300 / 1000 pcs. An optional switch at the start
 * of the estimate; when on, the AI prices EVERY quantity itself in the same call — per fabric / trim line a unit price,
 * yield and reason, plus the cut-make-pack CM and processes — weighing minimum orders, small-run waste, factory small-lot
 * charges and so on. The engine only does what it does for every sheet: price × yield, sums, quote coefficients, one-off
 * costs over the quantity. Off → one quantity, as before.
 */
import { cmPartsOf, isSet, setCmParts, splitLabour, TIER_PARTS } from "./costing.ts";
import { f2, f3, n } from "./num.ts";
import type { AllCmParts, PriceResult, Sheet, TierProc } from "./types.ts";

/** One quantity's per-piece CM parts, from the sheet's parts (amounts typed in RMB keep their RMB figure). */
function procOf(p: AllCmParts): TierProc {
  const out: TierProc = {};
  const cny: TierProc["cny"] = {};
  for (const k of TIER_PARTS) { out[k] = p[k].px; if (isSet(p[k].cny)) cny[k] = p[k].cny; }
  if (Object.keys(cny).length) out.cny = cny;
  return out;
}
/** Load one quantity's parts into the sheet's parts. Older tiers: cut-make-pack in tiers.cm, processes in proc. */
function loadProc(p: AllCmParts, pr: TierProc | undefined, cm: { px: string } | undefined): void {
  const labour = !!pr && (["cut", "sew", "pack"] as const).some((k) => pr[k] != null);
  if (!labour && cm) p.cmt = { ...p.cmt, px: cm.px, cny: undefined };
  if (!pr) return;
  // the AI's cutting / sewing / packing for this quantity replace an older one-figure cut-make-pack
  if (labour && pr.cmt == null) p.cmt = { ...p.cmt, px: "", cny: undefined };
  for (const k of TIER_PARTS) if (pr[k] != null) p[k] = { ...p[k], px: pr[k]!, cny: pr.cny?.[k] };
}

/** "100/300, 1000 pcs" → [100, 300, 1000] (sorted, no duplicates, at most 8). */
export function parseQtys(v: unknown): number[] {
  const raw = Array.isArray(v) ? v : String(v ?? "").split(/[^\d.]+/);
  return [...new Set(raw.map((x) => Math.round(n(x))).filter((x) => x > 0))].sort((a, b) => a - b).slice(0, 8);
}

/** The AI's tiers onto a freshly filled sheet (rows are in the reply's fabric / trim order). Mutates. */
export function applyTiers(s: Sheet, pr: Pick<PriceResult, "tiers">, qtys: number[]): void {
  if (!qtys.length) return;
  s.tiers = { qtys, cm: {}, proc: {}, note: {} };
  const list = pr.tiers || [];
  qtys.forEach((qty, ti) => {
    const t = list.find((x) => Math.round(n(x.qty)) === qty) || (list.every((x) => x.qty == null) ? list[ti] : undefined);
    if (!t) return;
    const q = String(qty);
    (["fab", "trim"] as const).forEach((k) => {
      const src = (k === "fab" ? t.fabrics : t.trims) || [];
      // the AI lists only the lines that change (i = position); a line it leaves out is the same as the main line
      const sparse = !src.length || src.some((x) => x && x.i != null);
      s[k].forEach((r, i) => {
        const x = sparse ? src.find((y) => y && Math.round(n(y.i)) === i) : src[i];
        if (sparse && !x) { r.tq = { ...(r.tq || {}), [q]: { px: r.px, yld: r.yld, why: "" } }; return; }
        if (!x || x.px == null) return;
        r.tq = { ...(r.tq || {}), [q]: { px: f3(x.px), yld: x.yield != null ? f3(x.yield) : r.yld, why: String(x.why || "") } };
      });
    });
    const pr: TierProc = {};
    if (t.cm_base != null && n(t.cm_base) > 0) {
      s.tiers!.cm[q] = { px: f2(t.cm_base), why: String(t.cm_why || "") };
      Object.assign(pr, splitLabour(t.cm_split, n(t.cm_base)), { cmt: "" }); // cutting / sewing / packing at this quantity (JF 2026-10-13)
    }
    if (t.process) for (const k of ["wash", "emb", "other"] as const) if (t.process[k] != null) pr[k] = n(t.process[k]) ? f2(t.process[k]) : "";
    if (Object.keys(pr).length) s.tiers!.proc![q] = pr;
    if (t.note) s.tiers!.note![q] = String(t.note);
  });
  // whatever the AI left out keeps the numbers it gave for the sheet's own quantity (copied now: the sheet will change)
  const p0 = cmPartsOf(s);
  for (const qty of qtys) {
    const q = String(qty);
    for (const k of ["fab", "trim"] as const) for (const r of s[k]) if (!r.tq?.[q]) r.tq = { ...(r.tq || {}), [q]: { px: r.px, yld: r.yld, why: "", none: true } };
    const labour = [p0.cut.px, p0.sew.px, p0.pack.px, p0.cmt.px].reduce((a, v) => a + n(v), 0);
    if (!s.tiers.cm[q] && labour) s.tiers.cm[q] = { px: f2(labour), why: "", none: true };
    s.tiers.proc![q] = { ...procOf(p0), ...(s.tiers.proc![q] || {}) };
  }
  // every quantity starts with the sheet's quote coefficients; the AM can change them per quantity (JF 2026-10-13)
  if (s.qcoef) s.tiers.coef = Object.fromEntries(qtys.map((q) => [String(q), { ...s.qcoef! }]));
  // the cost sheet shows one quantity: the smallest, until the AM picks another (JF 2026-10-12)
  switchTier(s, qtys[0]);
}

/**
 * Show another quantity in the cost sheet: the current lines / CM / processes are kept as that tier's numbers (so edits
 * made on the sheet stay with their quantity), then the chosen tier is loaded. Mutates.
 */
export function switchTier(s: Sheet, qty: number): void {
  const t = s.tiers;
  if (!t || !t.qtys.includes(qty)) return;
  const p = cmPartsOf(s);
  if (t.active != null) {
    const a = String(t.active);
    if (s.qcoef) t.coef = { ...(t.coef || {}), [a]: { ...s.qcoef } }; // each quantity keeps its own quote coefficients
    for (const k of ["fab", "trim"] as const) for (const r of s[k]) {
      const old = r.tq?.[a];
      const same = old && old.px === r.px && old.yld === r.yld;
      r.tq = { ...(r.tq || {}), [a]: { px: r.px, yld: r.yld, why: old?.why ?? "", ...(same && old?.none ? { none: true } : {}), ...(isSet(r.cny) ? { cny: r.cny } : {}) } };
    }
    const labour = [p.cut.px, p.sew.px, p.pack.px, p.cmt.px].some(isSet) ? f2([p.cut.px, p.sew.px, p.pack.px, p.cmt.px].reduce((x, v) => x + n(v), 0)) : "";
    if (labour) { const old = t.cm[a]; t.cm[a] = { px: labour, why: old?.why ?? "", ...(old?.none && old.px === labour ? { none: true } : {}) }; }
    t.proc = { ...(t.proc || {}), [a]: procOf(p) };
  }
  const q = String(qty);
  for (const k of ["fab", "trim"] as const) for (const r of s[k]) { const x = r.tq?.[q]; if (x) { r.px = x.px; r.yld = x.yld; r.cny = x.cny; } }
  const pr = t.proc?.[q];
  loadProc(p, pr, t.cm[q]);
  s.h.qty = q; // before the parts: per-order amounts are spread over this quantity
  const hadParts = !!s.cm.parts;
  setCmParts(s, p);
  if (!hadParts && !pr) delete s.cm.parts;
  if (t.coef?.[q]) s.qcoef = { ...t.coef[q] };
  t.active = qty;
}

export interface Tier { qty: number; sheet: Sheet; missing: number }

/**
 * The sheet at one quantity: each line's price / yield for that quantity, its CM and processes. A line the AI did not
 * price for this quantity (e.g. added by hand later) keeps the sheet's own numbers and is counted in `missing`.
 */
export function tierSheet(s: Sheet, qty: number): Tier {
  // the quantity shown above IS the cost sheet — with whatever the AM changed there
  if (s.tiers?.active === qty) {
    const o = structuredClone(s);
    o.tiers = undefined;
    for (const k of ["fab", "trim"] as const) for (const r of o[k]) delete r.tq;
    return { qty, sheet: o, missing: 0 };
  }
  const o = structuredClone(s);
  const q = String(qty);
  const tiers = s.tiers;
  o.tiers = undefined;
  o.h.qty = q;
  if (tiers?.coef?.[q]) o.qcoef = { ...tiers.coef[q] };
  let missing = 0;
  for (const k of ["fab", "trim"] as const) {
    for (const r of o[k]) {
      const x = r.tq?.[q];
      if (x) { r.px = x.px; r.yld = x.yld; r.cny = x.cny; r.q = { px: null, fin: r.q.fin, yld: null }; }
      if (!x || x.none) missing++;
      delete r.tq;
    }
  }
  const p = cmPartsOf(s);
  const cm = tiers?.cm[q];
  const pr = tiers?.proc?.[q];
  if ((!cm && [p.cmt.px, p.cut.px, p.sew.px, p.pack.px].some(isSet)) || cm?.none) missing++;
  loadProc(p, pr, cm);
  setCmParts(o, p);
  if (!s.cm.parts && !pr) delete o.cm.parts;
  o.cm.q = null; // a quoted CM typed for the sheet's own quantity does not carry over
  return { qty, sheet: o, missing };
}

export const tiersOf = (s: Sheet): Tier[] => (s.tiers?.qtys.length ? s.tiers.qtys.map((q) => tierSheet(s, q)) : []);
